<template>
  <button v-if="canShow" :type="type" class="kb-btn" :class="buttonClasses" :disabled="disabled || loading"
    @click="$emit('click', $event)">
    <!-- Left icon -->
    <span v-if="$slots.leftIcon" class="kb-btn__icon kb-btn__icon--left"
      :class="{ 'kb-btn__icon--slide': iconAnimation === 'slide' }" aria-hidden="true">
      <slot name="leftIcon" />
    </span>

    <!-- Label -->
    <span class="kb-btn__label-wrapper">
      <span class="kb-btn__label">
        <slot>{{ title }}</slot>
      </span>
    </span>


    <!-- Right icon -->
    <span v-if="$slots.rightIcon" class="kb-btn__icon kb-btn__icon--right"
      :class="{ 'kb-btn__icon--slide': iconAnimation === 'slide' }" aria-hidden="true">
      <slot name="rightIcon" />
    </span>

    <!-- Spinner -->
    <span v-if="loading" class="kb-btn__spinner" aria-hidden="true"></span>
  </button>
</template>

<script setup>
import { computed } from "vue";
import { usePermission } from "@/utils/permissions";
import { actionButtons } from "@/config/actionButtons";

/* global defineProps, defineEmits */
const props = defineProps({
  type: { type: String, default: "button" },
  title: { type: String, default: "" },
  variant: {
    type: String, default: "primary",    // primary | dark | secondary | muted | success | danger | outline | outline-danger | ghost | none | cancel
  },
  size: {
    type: String, default: "md",    // sm | md | lg
  },
  block: Boolean,
  disabled: Boolean,
  loading: Boolean,
  iconAnimation: {
    type: String,
    default: "slide", // slide | none
  },
  slideLabel: {
    type: Boolean,
    default: false, // پیش‌فرض به حالت غیر فعال و نمایش عنوان
  },
  slideDirection: {
    type: String,
    default: "right", // right | left
  },
  actionName: {
    type: String,
    default: "",
  },
});

defineEmits(["click"]);

const permissionStore = usePermission()

const resolvedActionName = computed(() => {
  const raw = (props.actionName ?? '').toString().trim()
  if (!raw) return ''

  const mapped = actionButtons.find(b => b.name === raw)
  return mapped?.actionName || raw
})

const canShow = computed(() => permissionStore.hasAccess(resolvedActionName.value))

const variantClass = computed(() => `kb-btn--${props.variant}`);
const sizeClass = computed(() => `kb-btn--${props.size}`);
const buttonClasses = computed(() => [
  variantClass.value,
  sizeClass.value,
  {
    block: props.block,
    loading: props.loading,
    'kb-btn--slide-label': props.slideLabel,
    [`kb-btn--slide-${props.slideDirection}`]: props.slideLabel,
  },
]);
</script>

<style scoped>
/* =========================
   Base
   ========================= */
.kb-btn {
  --bg: var(--background-color-button, #1476f3);
  --bg-hover: var(--background-color-button-hover, #136ee4);
  --text: var(--text-color-button, #ffffff);

  --border: transparent;
  --border-hover: transparent;

  --shadow: none;

  /* icon container tokens (USED) */
  --icon-bg: rgba(255, 255, 255, 0.6);
  --icon-border: rgba(207, 214, 227, 1);

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  cursor: pointer;

  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;

  transition:
    background-color 0.18s ease,
    color 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;

  user-select: none;
  position: relative;
  overflow: hidden;
  font-family: "IRANSans", "Vazir FD", "Vazir", sans-serif;
  margin: 0 2px;

  /* تغییر برای سازگاری با تولبار */
  height: auto;
  /* ارتفاع خودکار */
  min-height: 32px;
  /* حداقل ارتفاع برای حالت‌های عادی */
}

/* حالت خاص برای دکمه‌های داخل تولبار */
/* فقط دکمه‌های داخل تولبار */
.toolbar-button .kb-btn {
  height: 100%;
  min-height: 0;
  padding: 0 10px;
}

.toolbar-button {
  border-color: transparent;
}

.kb-btn:hover:not(:disabled) {
  background: var(--bg-hover);
  border-color: var(--border-hover);
}

.kb-btn:active:not(:disabled) {
  background: var(--bg-hover);
  box-shadow: none;
}

.kb-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus-outline, rgba(var(--background-color-button-rgb, 20, 116, 242), 0.18));
}

.kb-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.kb-btn.loading {
  padding-right: 44px;
}

.kb-btn__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* =========================
   Sizes
   ========================= */
.kb-btn--sm {
  padding: 4px 12px;
  font-size: 13px;
}

.kb-btn--md {
  padding: 5px 15px;
  font-size: 14px;
}

.kb-btn--lg {
  padding: 8px 18px;
  font-size: 15px;
}

.kb-btn.block {
  width: 100%;
}

/* =========================
   Icons (SVG-friendly) + BOX ALWAYS
   ========================= */
.kb-btn__icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--icon-border);
  background: var(--icon-bg);

  transition: transform 0.18s ease, background 0.18s ease,
    border-color 0.18s ease, color 0.18s ease;
}

.kb-btn__icon :deep(svg) {
  display: block;
  stroke: currentColor;
}

.toolbar-button .kb-btn__icon {
  border-radius: 50%;
  width: calc(var(--toolbar-height) - 12px);
  height: calc(var(--toolbar-height) - 12px);
}


/* Slide animation for prev/next (NO up/down) */
.kb-btn:hover:not(:disabled) .kb-btn__icon--left.kb-btn__icon--slide {
  transform: translateX(-3px);
}

.kb-btn:hover:not(:disabled) .kb-btn__icon--right.kb-btn__icon--slide {
  transform: translateX(3px);
}

/* =========================
   Spinner
   ========================= */
.kb-btn__spinner {
  position: absolute;
  right: 14px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.55);
  border-top-color: rgba(255, 255, 255, 1);
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* =========================
   Variants
   ========================= */

/* Primary (filled, gradient) */
.kb-btn--primary {
  --bg: var(--background-color-button, #1476f3);
  --bg-hover: var(--background-color-button-hover, #136ee4);
  --text: var(--text-color-button, #ffffff);
  --border: transparent;
  --border-hover: transparent;

  --icon-bg: rgba(255, 255, 255, 0.14);
  --icon-border: rgba(255, 255, 255, 0.35);
  box-shadow: none;
}

.kb-btn--primary:hover:not(:disabled) {
  color: var(--btn-text, var(--text-color-button, #ffffff));
}

/* Secondary */
.kb-btn--secondary {
  --bg: var(--background-color-button2, #9aa2ac);
  --bg-hover: var(--background-color-button-hover2, #82878f);
  --text: #000000;
  --border: transparent;
  --border-hover: transparent;

  --icon-bg: rgba(255, 255, 255, 0.14);
  --icon-border: rgba(255, 255, 255, 0.35);
}

.kb-btn--secondary:hover:not(:disabled) {
  color: #000000;
}

.kb-btn--dark {
  --bg: #1c407a;
  --bg-hover: #18335f;
  --text: #ffffff;
  --border: transparent;
  --border-hover: transparent;

  --icon-bg: rgba(255, 255, 255, 0.14);
  --icon-border: rgba(255, 255, 255, 0.35);
}

.kb-btn--dark:hover:not(:disabled) {
  color: #ffffff;
}

/* Success */
.kb-btn--success {
  --bg: var(--background-color-success, #008000);
  --bg-hover: var(--background-color-success-hover, #037003);
  --text: var(--text-color-button, #ffffff);
  --border: transparent;
  --border-hover: transparent;

  --icon-bg: rgba(255, 255, 255, 0.14);
  --icon-border: rgba(255, 255, 255, 0.35);
}

.kb-btn--success:hover:not(:disabled) {
  color: var(--btn-text, var(--text-color-button, #ffffff));
}

/* Danger (filled red) */
.kb-btn--danger {
  --bg: var(--background-color-danger, #f00000);
  --bg-hover: var(--background-color-danger-hover, #e60404);
  --text: var(--text-color-button, #ffffff);
  --border: transparent;
  --border-hover: transparent;

  --icon-bg: rgba(255, 255, 255, 0.14);
  --icon-border: rgba(255, 255, 255, 0.35);
}

.kb-btn--danger:hover:not(:disabled) {
  color: var(--btn-text, var(--text-color-button, #ffffff));
}

.kb-btn--muted,
.kb-btn--cancel {
  --bg: #9aa2ac;
  --bg-hover: #82878f;
  --text: #ffffff;
  --border: transparent;
  --border-hover: transparent;

  --icon-bg: rgba(255, 255, 255, 0.14);
  --icon-border: rgba(255, 255, 255, 0.35);
}

.kb-btn--muted:hover:not(:disabled),
.kb-btn--cancel:hover:not(:disabled) {
  color: #ffffff;
}

/* Outline (primary) */
.kb-btn--outline {
  --bg: transparent;
  --bg-hover: rgba(var(--background-color-button-rgb, 20, 116, 242), 0.12);
  --text: var(--primary, var(--background-color-button, #1474f2));
  --border: rgba(var(--background-color-button-rgb, 20, 116, 242), 0.45);
  --border-hover: var(--primary, var(--background-color-button, #1474f2));

  --icon-bg: rgba(var(--background-color-button-rgb, 20, 116, 242), 0.08);
  --icon-border: rgba(var(--background-color-button-rgb, 20, 116, 242), 0.35);
}

.kb-btn--outline:hover:not(:disabled) {
  color: var(--primary, var(--background-color-button, #1474f2));
  box-shadow: none;
}

/*  Outline Danger */
.kb-btn--outline-danger {
  --bg: transparent;
  --bg-hover: rgba(var(--background-color-danger-rgb, 220, 53, 69), 0.12);
  --text: var(--danger, var(--background-color-danger, #dc3545));

  --border: rgba(var(--background-color-danger-rgb, 220, 53, 69), 0.55);
  --border-hover: rgba(var(--background-color-danger-rgb, 220, 53, 69), 0.8);

  --icon-bg: rgba(var(--background-color-danger-rgb, 220, 53, 69), 0.08);
  --icon-border: rgba(var(--background-color-danger-rgb, 220, 53, 69), 0.35);
  
}

.kb-btn--outline-danger:hover:not(:disabled) {
  color: var(--danger, var(--background-color-danger, #dc3545));
  box-shadow: none;
}

/* Ghost (neutral) */
.kb-btn--ghost {
  --bg: inherit;
  --bg-hover: inherit;
  --text: var(--text-color-primary, #1f2937);

  --border: transparent;
  --border-hover: transparent;

  --icon-bg: rgba(var(--background-color-button-rgb, 20, 116, 242), 0.06);
  --icon-border: rgba(var(--text-color-secondary-rgb, 117, 117, 117), 0.18);
}

.kb-btn--ghost:hover:not(:disabled) {
  color: var(--text-color-primary, #1f2937);
  box-shadow: none;
}

.kb-btn--none {
  --bg: inherit;
  --bg-hover: inherit;
  --text: #ffffff;
  --border: transparent;
  --border-hover: transparent;
  box-shadow: none;
}

/* =========================
   Slide Label (NEW)
   ========================= */

.kb-btn--slide-label {
  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

/* wrapper کنترل انیمیشن */
/* overflow: hidden; */
.kb-btn__label-wrapper {
  display: inline-flex;
  align-items: center;
  max-width: 0;
  opacity: 0;

  transition:
    max-width 220ms ease,
    opacity 160ms ease,
    margin 200ms ease,
    transform 220ms ease;
}

/* خود متن */
.kb-btn__label {
  white-space: nowrap;
}

/* =========================
   Slide Label (NEW)
   ========================= */

/* wrapper کنترلی برای انیمیشن */
.kb-btn__label-wrapper {
  /* overflow: hidden; */
  display: inline-flex;
  align-items: center;
  max-width: 200px;
  /* پیش‌فرض برای نمایش کامل */
  opacity: 1;
  /* پیش‌فرض برای نمایش کامل */
  transition:
    max-width 220ms ease,
    opacity 160ms ease,
    margin 200ms ease,
    transform 220ms ease;
}

/* -----------------------
    SLIDE LABEL
   ----------------------- */

.kb-btn--slide-label:not(:hover) .kb-btn__label-wrapper {
  max-width: 0;
  opacity: 0;
}

.kb-btn--slide-label:hover:not(:disabled) {
  /* رنگ Border را هنگام hover تنظیم کن */
  border-color: var(--border-hover, var(--border));

  /* مهم: تمام transition‌ها را مجدداً لیست کنید، اما فقط border-color را تاخیر دهید. */
  transition:
    /* سایر transitionها را بدون تغییر نگه دارید (مثلاً 0ms تاخیر) */
    background 0.18s ease 0ms,
    box-shadow 0.18s ease 0ms,
    transform 0.18s ease 0ms,
    /* Border-color: با تاخیر 250 میلی‌ثانیه ظاهر شود. */
    border-color 0.1s ease 250ms;
}

/* ---------- SLIDE RIGHT ---------- */
.kb-btn--slide-right .kb-btn__label-wrapper {
  transform: translateX(-6px);
}

.kb-btn--slide-right:hover .kb-btn__label-wrapper {
  max-width: 200px;
  opacity: 1;
  margin-left: 8px;
  transform: translateX(0);
}

/* ---------- SLIDE LEFT ---------- */
.kb-btn--slide-left .kb-btn__label-wrapper {
  order: -1;
  transform: translateX(6px);
}

.kb-btn--slide-left:hover .kb-btn__label-wrapper {
  max-width: 200px;
  opacity: 1;
  margin-right: 8px;
  transform: translateX(0);
}
</style>
